import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { Container, CssBaseline, ThemeProvider, createTheme, AppBar, Toolbar, Typography, Button } from '@mui/material';
import SutraList from './components/SutraList';
import Login from './components/Login';
import { sutraApi } from './services/sutras';
import MeaningsList from './components/MeaningsList';
import TransliterationsList from './components/TransliterationsList';
import InterpretationsList from './components/InterpretationsList';
import { BhashyamsList } from './components/BhashyamsList';
import AudiosList from './components/AudiosList';
import { AuthProvider, useAuth } from './contexts/AuthContext';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
});

const PrivateRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" />;
};

const InterpretationsListWrapper: React.FC = () => {
  const params = useParams();
  return (
    <InterpretationsList
      projectName={params.projectName || ''}
      section={params.section || ''}
      chapter={parseInt(params.chapter || '0')}
      number={parseInt(params.number || '0')}
    />
  );
};

const BhashyamsListWrapper: React.FC = () => {
  const params = useParams();
  return (
    <BhashyamsList
      projectName={params.projectName || ''}
      section={params.section || ''}
      chapter={parseInt(params.chapter || '0')}
      number={parseInt(params.number || '0')}
    />
  );
};

const AudiosListWrapper: React.FC = () => {
  const params = useParams();
  return (
    <AudiosList
      projectName={params.projectName || ''}
      section={params.section || ''}
      chapter={parseInt(params.chapter || '0')}
      number={parseInt(params.number || '0')}
    />
  );
};

const MeaningsListWrapper: React.FC = () => {
  return <MeaningsList />;
};

const TransliterationsListWrapper: React.FC = () => {
  return <TransliterationsList />;
};

const AppContent: React.FC = () => {
  const { isAuthenticated, logout } = useAuth();

  const handleLogout = () => {
    sutraApi.logout();
    logout();
    window.location.href = '/login';
  };

  return (
    <>
      <AppBar position="static">
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Upanishads Sutra Manager
          </Typography>
          {isAuthenticated && (
            <Button color="inherit" onClick={handleLogout}>
              Logout
            </Button>
          )}
        </Toolbar>
      </AppBar>
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Routes>
          <Route
            path="/"
            element={
              <PrivateRoute>
                <SutraList />
              </PrivateRoute>
            }
          />
          <Route path="/login" element={<Login />} />
          <Route path="/meanings/:projectName/:section/:chapter/:sutraNumber" element={<MeaningsListWrapper />} />
          <Route path="/transliterations/:projectName/:section/:chapter/:sutraNumber" element={<TransliterationsListWrapper />} />
          <Route
            path="/projects/:projectName/:section/sutras/:chapter/:number/interpretations"
            element={
              <PrivateRoute>
                <InterpretationsListWrapper />
              </PrivateRoute>
            }
          />
          <Route
            path="/projects/:projectName/:section/sutras/:chapter/:number/bhashyams"
            element={
              <PrivateRoute>
                <BhashyamsListWrapper />
              </PrivateRoute>
            }
          />
          <Route
            path="/projects/:projectName/:section/sutras/:chapter/:number/audios"
            element={
              <PrivateRoute>
                <AudiosListWrapper />
              </PrivateRoute>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Container>
    </>
  );
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router basename="/">
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </Router>
    </ThemeProvider>
  );
}

export default App;
